import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  ArrowLeft, Package, Wrench, ExternalLink, MapPin, Edit, Trash2, Loader2,
  History, PlusCircle, MinusCircle, ArchiveRestore,
} from 'lucide-react';
import AddProductDialog from '../../components/admin/inventory/AddProductDialog';
import ItemPhotoPanel from '../../components/admin/inventory/ItemPhotoPanel';
import { invPhoto } from '../../components/admin/inventory/photos';
import AddEquipmentDialog from '../../components/admin/inventory/AddEquipmentDialog';
import RestockDialog from '../../components/admin/inventory/RestockDialog';
import AdjustStockDialog from '../../components/admin/inventory/AdjustStockDialog';
import LogPastPurchaseDialog from '../../components/admin/inventory/LogPastPurchaseDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInventoryDetailPage = ({ kind = 'product' }) => {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/inventory');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [item, setItem] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [restockOpen, setRestockOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [logPastOpen, setLogPastOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const path = kind === 'equipment' ? 'equipment' : 'products';

  const fetchItem = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        axios.get(`${API_URL}/api/admin/inventory/${path}/${id}`, { headers }),
        axios.get(`${API_URL}/api/admin/inventory/${path}/${id}/activity`, { headers }),
      ]);
      setItem(r.data);
      setActivity(a.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Not found');
      navigate('/admin/inventory');
    } finally { setLoading(false); }
  }, [path, id, headers, navigate]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  const buyAgain = () => {
    if (item?.purchase_url) window.open(item.purchase_url, '_blank', 'noopener');
    else if (item?.vendor_name || item?.store_address) toast.info(`${item.vendor_name || ''}${item.store_address ? ` — ${item.store_address}` : ''}`, { duration: 6000 });
    else toast.error('No purchase URL or store info saved');
  };

  const del = async () => {
    try {
      await axios.delete(`${API_URL}/api/admin/inventory/${path}/${id}`, { headers });
      toast.success(kind === 'equipment' ? 'Equipment deleted' : 'Product deleted');
      navigate('/admin/inventory');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  if (loading || !item) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
      </div>
    );
  }

  const isProduct = kind === 'product';
  const statusColor =
    item.stock_status === 'In Stock' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
    item.stock_status === 'Low Stock' ? 'bg-amber-50 text-amber-700 border-amber-200' :
    item.stock_status === 'Out of Stock' ? 'bg-red-50 text-red-700 border-red-200' :
    'bg-zinc-100 text-zinc-700 border-zinc-200';

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 sticky top-12 md:top-0 z-30">
        <div className="max-w-[1680px] mx-auto px-3 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={back} data-testid="detail-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          {item.photo_url ? <img src={invPhoto(item.photo_url)} alt="" className="w-12 h-12 rounded-lg bg-[#f4f2ed] object-contain" /> : <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center">{isProduct ? <Package className="w-5 h-5 text-zinc-400" /> : <Wrench className="w-5 h-5 text-zinc-400" />}</div>}
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-lg font-heading text-zinc-900 truncate">{item.name}</h1>
            <p className="text-[11px] text-zinc-500">
              {isProduct && item.brand ? `${item.brand} · ` : ''}{isProduct ? item.category : `${item.condition} · ${item.status}`}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="detail-edit"><Edit className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Edit</span></Button>
            {isProduct && <Button size="sm" className="bg-[#F56F05] hover:bg-[#e06505] text-white" onClick={() => setRestockOpen(true)} data-testid="detail-restock">Restock</Button>}
            <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => setConfirmDelete(true)} data-testid="detail-delete"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1680px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 grid grid-cols-1 lg:grid-cols-10 gap-4 lg:gap-6">
        {/* Main info */}
        <div className="lg:col-span-7 min-w-0 space-y-4">
          <div className="sm:max-w-xs"><ItemPhotoPanel kind={isProduct ? 'product' : 'equipment'} item={item} headers={headers} onChanged={fetchItem} /></div>
          {isProduct && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Quantity" value={item.quantity_on_hand} />
                <Stat label="Reorder At" value={item.reorder_level} />
                <Stat label="Replacement" value={`$${(item.current_replacement_cost || 0).toFixed(2)}`} />
                <div>
                  <p className="text-[10px] uppercase font-semibold text-zinc-500">Status</p>
                  <Badge variant="outline" className={`${statusColor} text-[11px] mt-1`}>{item.stock_status}</Badge>
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Purchasing</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              <Row label="Vendor / Store" value={item.vendor_name} />
              <Row label="Store Address" value={item.store_address} icon={MapPin} />
              <Row label="Purchase URL" value={item.purchase_url}
                   custom={item.purchase_url && <a href={item.purchase_url} target="_blank" rel="noreferrer" className="text-[#F56F05] hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Open</a>} />
              <Row label={isProduct ? 'Latest Replacement Cost' : 'Purchase Price'} value={`$${(isProduct ? item.current_replacement_cost : item.purchase_price)?.toFixed?.(2) || '—'}`} />
              {!isProduct && item.current_replacement_cost != null && <Row label="Current Replacement Cost" value={`$${item.current_replacement_cost.toFixed(2)}`} />}
              <Row label="Date Purchased" value={item.date_purchased} />
              <Row label="Current Location" value={item.current_location} icon={MapPin} />
              {item.notes && <Row label="Notes" value={item.notes} />}
            </CardContent>
          </Card>
        </div>

        {/* Actions + Activity */}
        <div className="lg:col-span-3 min-w-0 space-y-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Actions</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {isProduct && <Button variant="outline" className="w-full justify-start" onClick={() => setAdjustOpen(true)} data-testid="detail-adjust"><MinusCircle className="w-4 h-4 mr-2" /> Adjust Stock</Button>}
              <Button variant="outline" className="w-full justify-start" onClick={() => setLogPastOpen(true)} data-testid="detail-log-past"><History className="w-4 h-4 mr-2" /> Log Past Purchase</Button>
              <Button variant="outline" className="w-full justify-start" onClick={buyAgain} data-testid="detail-buy-again"><ExternalLink className="w-4 h-4 mr-2" /> Buy Again</Button>
              {isProduct && <Button variant="outline" className="w-full justify-start" onClick={() => setRestockOpen(true)}><ArchiveRestore className="w-4 h-4 mr-2" /> Restock</Button>}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Inventory Activity</CardTitle></CardHeader>
            <CardContent>
              {activity.length === 0 ? (
                <p className="text-xs text-zinc-500 py-3 text-center">No activity yet.</p>
              ) : (
                <ol className="space-y-2 max-h-[500px] overflow-y-auto">
                  {activity.map(a => (
                    <li key={a.id} className="text-xs border-b border-zinc-50 last:border-0 pb-2" data-testid={`activity-entry-${a.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">{a.txn_type}</Badge>
                            {a.quantity_delta != null && (
                              <span className={`font-bold ${a.quantity_delta > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {a.quantity_delta > 0 ? '+' : ''}{a.quantity_delta}
                              </span>
                            )}
                            {a.amount_paid != null && <span className="text-zinc-700">${a.amount_paid.toFixed(2)}</span>}
                          </div>
                          <p className="text-[10px] text-zinc-500 mt-0.5">{a.actor_name || 'System'} · {a.purchase_date || new Date(a.created_at).toLocaleDateString()}</p>
                          {a.reason && <p className="text-[10px] text-zinc-500">Reason: {a.reason}</p>}
                          {a.notes && <p className="text-[10px] text-zinc-500 italic mt-1">{a.notes}</p>}
                        </div>
                        {a.vendor_name && <span className="text-[10px] text-zinc-400 flex-shrink-0">{a.vendor_name}</span>}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Dialogs */}
      {isProduct ? (
        <AddProductDialog open={editOpen} onOpenChange={setEditOpen} headers={headers} product={item} onSaved={fetchItem} />
      ) : (
        <AddEquipmentDialog open={editOpen} onOpenChange={setEditOpen} headers={headers} equipment={item} onSaved={fetchItem} />
      )}
      {isProduct && (
        <>
          <RestockDialog product={restockOpen ? item : null} onOpenChange={(o) => setRestockOpen(o)} headers={headers} onSaved={fetchItem} />
          <AdjustStockDialog product={adjustOpen ? item : null} onOpenChange={(o) => setAdjustOpen(o)} headers={headers} onSaved={fetchItem} />
        </>
      )}
      <LogPastPurchaseDialog target={logPastOpen ? { type: kind, id, name: item.name } : null} onOpenChange={(o) => setLogPastOpen(o)} headers={headers} onSaved={fetchItem} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {item.name}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the {kind}. Inventory activity history is preserved.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={del}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div><p className="text-[10px] uppercase font-semibold text-zinc-500">{label}</p><p className="text-lg font-bold text-zinc-900">{value ?? '—'}</p></div>
);
const Row = ({ label, value, icon: Icon, custom }) => {
  if (!value && !custom) return null;
  return (
    <div className="flex items-start justify-between gap-3 pb-2 border-b border-zinc-50 last:border-0">
      <p className="text-[11px] uppercase text-zinc-500 font-semibold flex-shrink-0">{label}</p>
      <div className="text-right text-zinc-800 min-w-0 flex-1">
        {custom || <span className="inline-flex items-start gap-1 justify-end">{Icon && <Icon className="w-3 h-3 text-zinc-400 mt-1 flex-shrink-0" />}<span className="break-words min-w-0">{value}</span></span>}
      </div>
    </div>
  );
};

export default AdminInventoryDetailPage;
